import { useDomainContext } from "@hooks/useDomainContext";
import { Buyer } from "@hooks/useForm/types";
import React, { useMemo } from "react";
import styles from "./style.module.scss";

export default function BuyerCallNotice({
    winnerBuyer,
}: {
    winnerBuyer: Buyer;
}) {
    const { category, domain } = useDomainContext();

    const hasWinnerBuyerOffer = useMemo(() => {
        return domain.defaultOffers?.find((offer) => {
            return offer.buyer?.id === Number(winnerBuyer.buyerId);
        });
    }, []);

    const msg: string = useMemo(() => {
        if (hasWinnerBuyerOffer) {
            return (
                category?.config?.callNoticeDescription ||
                "A representative from {name} will be with you shortly. Thanks for your patience. If you miss the call, you can reach out to your matched service provider using the number below."
            );
        }
        return (
            category?.config?.callNoticeDescriptionNoOfferToDisplay ||
            "A representative from {name} will be with you shortly. Thanks for your patience."
        );
    }, [hasWinnerBuyerOffer]);

    if (winnerBuyer.buyerId)
        return (
            <div className="p-5 lg:p-10 bg-[#f8f8f8] text-center my-8 lg:my-16 flex flex-col items-center">
                <div className="mb-8">
                    <Phone />
                </div>
                <h1
                    className="text-2xl lg:text-3xl font-semibold"
                    dangerouslySetInnerHTML={{
                        __html: (
                            category?.config?.callNoticeTitle ||
                            "Hold Tight, <span style='color: #5fa347'>Connecting</span> Your Call"
                        ).replace("{name}", winnerBuyer?.displayName),
                    }}
                />
                <p
                    className="mt-4 max-w-full lg:max-w-[80%] font-light"
                    dangerouslySetInnerHTML={{
                        __html: msg?.replace(
                            "{name}",
                            `<span style='color: #5fa347;font-weight: 600'>${winnerBuyer?.displayName}</span>`,
                        ),
                    }}
                />
            </div>
        );
}

const Phone = () => (
    <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clip-path="url(#clip0_1493_272)">
            <path
                d="M37.327 31.5246C37.1933 31.4932 37.0619 31.4433 36.9355 31.3741C36.182 30.9598 35.9067 30.0071 36.3205 29.2463C37.4915 27.0942 37.7597 24.6097 37.0758 22.2507C36.3885 19.8799 34.8248 17.9255 32.6729 16.7476C31.9186 16.3348 31.6415 15.3827 32.0542 14.621C32.4668 13.8592 33.4133 13.5767 34.1671 13.9895C37.0496 15.5672 39.144 18.185 40.0647 21.3605C40.982 24.524 40.6216 27.8573 39.0497 30.7466C38.705 31.3798 37.9913 31.6807 37.327 31.5246Z"
                fill="#5FA347"
                className={styles.bigWave}
            />
            <path
                d="M31.1108 28.1219C30.977 28.0905 30.8455 28.0406 30.7192 27.9714C29.9657 27.5571 29.6904 26.6044 30.1043 25.8436C30.3709 25.3535 30.4322 24.7888 30.277 24.2533C30.1205 23.7138 29.7635 23.2684 29.2715 22.9991C28.5172 22.5863 28.2401 21.6342 28.6528 20.8725C29.0654 20.1106 30.012 19.8278 30.7657 20.241C31.9883 20.9101 32.8762 22.019 33.266 23.3631C33.6545 24.7031 33.5008 26.1169 32.8334 27.3437C32.4886 27.977 31.7749 28.2779 31.1108 28.1219Z"
                fill="#5FA347"
                className={styles.smallWave}
            />
            <path
                className={styles.shake}
                d="M46.7813 41.2465L55.7325 43.3489C56.5691 43.5454 57.408 43.0206 57.6062 42.1769C57.8043 41.3332 57.2868 40.4898 56.4502 40.2934L47.499 38.191C44.4558 37.4762 41.3952 39.3853 40.6762 42.4466C39.9572 45.5078 41.8481 48.5798 44.8911 49.2946L49.8708 50.4641C51.2253 50.7823 52.0678 52.1464 51.7488 53.5048C51.4297 54.8633 50.068 55.7097 48.7135 55.3916L33.6263 51.848C34.8165 51.0399 35.7837 49.9314 36.4372 48.6021C37.4269 46.5896 37.5788 44.312 36.8651 42.1889L35.0438 36.7711C33.9934 33.6468 30.6075 31.9679 27.4954 33.0297L25.4525 33.7265L20.9085 20.2091L22.9423 19.5153C24.4529 18.9999 25.6744 17.924 26.3816 16.4858C27.0876 15.05 27.196 13.4253 26.687 11.9107L24.8657 6.49293C23.3925 2.11054 18.6431 -0.243271 14.2781 1.24583L9.98988 2.70888C7.87156 3.43151 6.15896 4.93982 5.1675 6.95618C4.17784 8.96856 4.0259 11.2463 4.73955 13.3694L16.7389 49.0651C17.6956 51.9111 20.0334 53.9013 22.7509 54.5394C24.218 54.8838 25.7962 54.8344 27.3264 54.3123L28.6792 53.8508C28.8005 53.9178 28.9328 53.9696 29.0742 54.0028L47.9959 58.4469C51.021 59.1574 54.0637 57.2596 54.7785 54.2163C55.4932 51.173 53.6136 48.119 50.5884 47.4085L45.6088 46.2389C44.2362 45.9165 43.3826 44.5346 43.7059 43.1581C44.0292 41.7816 45.4088 40.9241 46.7813 41.2465ZM26.3379 51.3411C23.5963 52.2763 20.6131 50.7995 19.6884 48.0485L7.68889 12.353C7.24227 11.0244 7.3372 9.59937 7.95618 8.34052C8.5769 7.07798 9.6503 6.13324 10.9784 5.68003L15.2665 4.21712C16.2287 3.88886 17.2201 3.85762 18.1423 4.07423C19.8483 4.47492 21.3161 5.724 21.9162 7.50965L23.7375 12.9274C23.9795 13.6472 23.9281 14.4195 23.5927 15.1016C23.2561 15.786 22.674 16.2984 21.9536 16.5442L18.4392 17.7432C18.047 17.877 17.723 18.1625 17.5391 18.5365C17.3552 18.9106 17.3263 19.3425 17.4589 19.737L23.0005 36.2224C23.2754 37.0402 24.1558 37.4773 24.9695 37.1996L28.484 36.0007C29.9731 35.493 31.5927 36.2943 32.0945 37.7876L33.9158 43.2054C34.3624 44.5339 34.2675 45.9588 33.6485 47.2178C33.0278 48.4803 31.9544 49.4251 30.6264 49.8781L26.3379 51.3411Z"
                fill="black"
            />
        </g>
    </svg>
);
